import { useEffect, useRef, useState } from "react";
import { DraftGallery } from "../components/DraftGallery";
import { copy } from "../copy";
import { draftBodyText, type TopicDraft } from "../domain/draft";
import { topicTitle } from "../domain/display";
import { topicAccount } from "../domain/accounts";
import { formatWhen } from "../lib/format";
import { ApiError } from "../lib/api";
import { currentGuide, latestScore, scoreTotal } from "../domain/research";
import {
  imageTarget,
  readRevisions,
  REVIEW_TARGETS,
  rewritingLabel,
  rewritingVersion,
  slidesFor,
  submittedImageLine,
  versionChoices,
  versionLabel,
  type DraftRevision,
} from "../domain/revisions";
import { topicsForAccount } from "../domain/selectors";
import { reviewTopics } from "../domain/worklist";
import { useApp } from "../state/store";
import type { Topic } from "../types";

export function ReviewDesk() {
  const app = useApp();
  const accountTopics = topicsForAccount(app.state.topics, app.accountId);
  const pending = reviewTopics(accountTopics);
  const requested = new URLSearchParams(app.href.split("?")[1] ?? "").get("topic");
  const selected = accountTopics.find((topic) => topic.id === requested) ?? pending[0] ?? null;
  const listed = selected && !pending.some((topic) => topic.id === selected.id) ? [selected, ...pending] : pending;

  return (
    <div className="review-board">
      <div className="todo-list review-list">
        {listed.length === 0 ? <p className="muted">{copy.today.emptyReview}</p> : null}
        {listed.map((topic) => {
          const version = rewritingVersion(readRevisions(topic.id), topic.status, app.state.tasks ?? [], topic.id);
          return (
            <button
              key={topic.id}
              type="button"
              className={selected?.id === topic.id ? "todo-row as-link is-current" : "todo-row as-link"}
              onClick={() => app.navigate("review", `topic=${encodeURIComponent(topic.id)}`)}
            >
              <span className="todo-copy">
                <b>{topicTitle(topic)}</b>
              </span>
              <span className="muted">{version ? rewritingLabel(version) : scoreText(app, topic.id)}</span>
            </button>
          );
        })}
      </div>
      {selected ? <ReviewStage key={selected.id} topic={selected} /> : null}
    </div>
  );
}

function scoreText(app: ReturnType<typeof useApp>, topicId: string): string {
  const score = latestScore(app.state.postScores ?? [], topicId);
  return score ? `${scoreTotal(score)}/40` : copy.research.unscored;
}

function ReviewStage({ topic, onDone }: { topic: Topic; onDone?: (nextId: string | null) => void }) {
  const app = useApp();
  const [draft, setDraft] = useState<TopicDraft | null>(null);
  const [revisions, setRevisions] = useState<DraftRevision[]>(() => readRevisions(topic.id));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [evaluation, setEvaluation] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [targets, setTargets] = useState<string[]>([]);
  const [picked, setPicked] = useState<number | null>(null);
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [slideIndex, setSlideIndex] = useState(1);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState(topicTitle(topic));
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");

  const loadDraft = useRef(app.loadDraft);
  const loadRevisions = useRef(app.loadRevisions);
  loadDraft.current = app.loadDraft;
  loadRevisions.current = app.loadRevisions;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void loadDraft.current(topic.id)
      .then((next) => {
        if (cancelled) return;
        setDraft(next);
        setError("");
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setDraft(null);
        setError(caught instanceof ApiError ? caught.message : copy.flow.localDraft);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    void loadRevisions.current(topic.id).then((rows) => {
      if (!cancelled) setRevisions(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [topic.id, topic.title]);

  const live = draft
    ? { title: draft.title || topicTitle(topic), body: draftBodyText(draft), tags: draft.tags, images: draft.images.map((image) => ({ url: image.url, caption: image.caption })) }
    : null;
  const slides = versionChoices(revisions, live);
  const viewing = slides.find((slide) => slide.version === picked) ?? slides[slides.length - 1] ?? null;
  const latest = slides[slides.length - 1] ?? null;
  const onLatest = !latest || viewing?.version === latest.version;
  const images = slidesFor(viewing?.images ?? []);
  const imageLine = loading ? null : submittedImageLine(images.length);
  const produced = rewritingVersion(revisions, topic.status, app.state.tasks ?? [], topic.id);

  const score = latestScore(app.state.postScores ?? [], topic.id);
  const guide = score?.styleVersionId ? (app.state.styleGuides ?? []).find((item) => item.id === score.styleVersionId) : null;
  const style = currentGuide(app.state.styleGuides ?? [], topicAccount(topic));
  const parts = score
    ? [
        { label: copy.research.subs.sense, value: score.sense, reason: score.senseReason },
        { label: copy.research.subs.ai, value: score.ai, reason: score.aiReason },
        { label: copy.research.subs.save, value: score.saveValue, reason: score.saveReason },
        { label: copy.research.subs.cover, value: score.cover, reason: score.coverReason },
      ]
    : [];
  const shownTitle = editing ? title : viewing?.title || topicTitle(topic);
  const shownBody = editing ? body : viewing?.body || (error ? error : loading ? copy.review.loading : copy.flow.bodyMissing);
  const shownTags = editing ? tags : (viewing?.tags ?? []).join(" ");
  function toggleTarget(target: string) {
    setTargets((current) => (current.includes(target) ? current.filter((item) => item !== target) : [...current, target]));
  }

  function markImage(index: number) {
    const target = imageTarget(index);
    setTargets((current) => (current.includes(target) ? current : [...current, target]));
  }

  async function approve(withEdit: boolean) {
    setBusy(true);
    setFormError("");
    try {
      const tagList = tags.split(/[\s,，]+/).map((item) => item.trim()).filter(Boolean);
      await app.approveEdited(topic.id, withEdit ? { title, body, tags: tagList } : null);
      const rest = reviewTopics(topicsForAccount(app.state.topics, app.accountId)).find((item) => item.id !== topic.id);
      if (onDone) onDone(rest?.id ?? null);
      else app.navigate("review", rest ? `topic=${encodeURIComponent(rest.id)}` : "");
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : copy.review.failed);
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    if (!suggestion.trim()) {
      setFormError(copy.flow.reasonNeeded);
      return;
    }
    setBusy(true);
    setFormError("");
    setNotice("");
    try {
      const result = await app.regenerateFromAdvice({
        topicId: topic.id,
        title: viewing?.title || shownTitle,
        body: viewing?.body || shownBody,
        tags: viewing?.tags ?? [],
        images: viewing?.images ?? [],
        evaluation,
        suggestion,
        targets,
        styleVersionId: style?.id ?? null,
      });
      setRevisions(readRevisions(topic.id));
      const lines = [
        result.waiting ? copy.flow.waiting : rewritingLabel(result.revision.version + 1),
        result.remote && !result.stored ? copy.flow.migration : "",
        !result.remote ? copy.flow.localRewrite : "",
      ].filter(Boolean);
      setNotice(lines.join(" "));
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : copy.review.failed);
    } finally {
      setBusy(false);
    }
  }

  const imageChoices = Array.from(
    new Set([...targets.filter((target) => /^第 \d+ 张$/.test(target)), ...(images.length ? [imageTarget(Math.min(slideIndex, images.length))] : [])]),
  );

  return (
    <section className="review-stage">
      <article className="phone-note" aria-label="笔记预览">
        <DraftGallery images={images} onMark={markImage} onIndex={setSlideIndex} />
        {imageLine ? <p className="phone-honest">{imageLine}</p> : null}
        {editing ? (
          <div className="phone-edit">
            <label>
              {copy.flow.title}
              <input value={title} maxLength={80} onChange={(event) => setTitle(event.target.value)} />
            </label>
            <label>
              {copy.flow.body}
              <textarea value={body} rows={6} onChange={(event) => setBody(event.target.value)} />
            </label>
            <label>
              {copy.flow.tags}
              <input value={tags} onChange={(event) => setTags(event.target.value)} />
            </label>
          </div>
        ) : (
          <div className="phone-copy">
            <h2>{shownTitle}</h2>
            <p>{shownBody}</p>
            {shownTags ? <p className="phone-tags">{shownTags.split(/[\s,，]+/).filter(Boolean).map((tag) => `#${tag.replace(/^#/, "")}`).join(" ")}</p> : null}
          </div>
        )}
      </article>
      <div className="review-side">
        <div className="review-score">
          <h2>
            {copy.research.sense}
            {score ? (
              <>
                {" "}
                <b className="num">{scoreTotal(score)}</b> <span className="muted">/ 40</span>
              </>
            ) : null}
          </h2>
          {score ? (
            <>
              <p className="muted">
                {guide ? `${copy.research.against} · ${formatWhen(guide.createdAt)}` : score.styleVersionId ? `${copy.research.against} · ${score.styleVersionId}` : copy.research.noGuide}
              </p>
              <div className="sense-grid">
                {parts.map((part) => (
                  <div key={part.label} className="sense-item">
                    <span>{part.label}</span>
                    <b className="num">{part.value}</b>
                    <small>{part.reason.trim() || copy.dash}</small>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="muted">{copy.research.unscored}</p>
          )}
        </div>
        {produced ? <p className="rewrite-flag">{rewritingLabel(produced)}</p> : null}
        {slides.length ? (
          <div className="version-switch" role="group" aria-label="历史版本">
            {slides.map((slide) => (
              <button key={slide.version} type="button" aria-pressed={viewing?.version === slide.version} onClick={() => setPicked(slide.version)}>
                {versionLabel(slide.version)}
              </button>
            ))}
          </div>
        ) : null}
        <label className="review-comment">
          <span>{copy.flow.evaluation}</span>
          <textarea value={evaluation} rows={3} maxLength={2000} onChange={(event) => setEvaluation(event.target.value)} />
        </label>
        <label className="review-comment">
          <span>{copy.flow.suggestion}</span>
          <textarea
            value={suggestion}
            rows={4}
            maxLength={2000}
            placeholder={copy.review.commentPlaceholder}
            onChange={(event) => setSuggestion(event.target.value)}
          />
        </label>
        <fieldset className="target-list">
          <legend>{copy.flow.targets}</legend>
          {REVIEW_TARGETS.map((target) => (
            <label key={target}>
              <input type="checkbox" checked={targets.includes(target)} onChange={() => toggleTarget(target)} />
              {target}
            </label>
          ))}
          {imageChoices.map((target) => (
            <label key={target}>
              <input type="checkbox" checked={targets.includes(target)} onChange={() => toggleTarget(target)} />
              {target}
            </label>
          ))}
        </fieldset>
        {notice ? <p className="muted">{notice}</p> : null}
        {formError ? <p className="inline-error">{formError}</p> : null}
        {!onLatest ? <p className="muted">正在看{viewing ? versionLabel(viewing.version) : ""}。通过会用最新一版。</p> : null}
        <div className="review-actions">
          <button type="button" className="button primary" disabled={busy || topic.status !== "待审阅"} onClick={() => void regenerate()}>
            {copy.flow.regenerate}
          </button>
          {editing ? (
            <>
              <button type="button" className="button line" disabled={busy || !onLatest} onClick={() => void approve(true)}>
                {copy.flow.confirmPass}
              </button>
              <button type="button" className="button ghost" disabled={busy} onClick={() => setEditing(false)}>
                {copy.flow.cancelEdit}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="button line" disabled={busy || !onLatest} onClick={() => void approve(false)}>
                {copy.flow.pass}
              </button>
              <button
                type="button"
                className="button line"
                disabled={busy || !onLatest}
                onClick={() => {
                  setTitle(viewing?.title || topicTitle(topic));
                  setBody(viewing?.body || "");
                  setTags((viewing?.tags ?? []).join(" "));
                  setEditing(true);
                }}
              >
                {copy.flow.editPass}
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export function PhoneReviewDrawer({ topic, onClose }: { topic: Topic; onClose?: (nextId: string | null) => void }) {
  const app = useApp();
  function close(nextId: string | null) {
    if (onClose) onClose(nextId);
    else app.navigate("today", nextId ? `topic=${encodeURIComponent(nextId)}` : "");
  }
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (onClose) onClose(null);
      else app.navigate("today");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [app, onClose]);

  return (
    <div className="drawer-backdrop" onMouseDown={() => close(null)}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label="审稿" onMouseDown={(event) => event.stopPropagation()}>
        <header className="sheet-head">
          <h2>审稿</h2>
          <button type="button" className="button ghost" onClick={() => close(null)}>
            {copy.close}
          </button>
        </header>
        <ReviewStage topic={topic} onDone={close} />
      </div>
    </div>
  );
}
